const site = {
  name: 'Meina Ghafouri',
  blurb:
    'Meina Ghafouri builds go-to-market systems for AI infrastructure and vector search. Product marketer at Qdrant. Engineering background and creative technologist. Previously Teleport, Okta, and Auth0.',
  links: [
    { label: 'meina.me', url: 'https://meina.me/' },
    { label: 'LinkedIn', url: 'https://www.linkedin.com/in/meina-ghafouri' },
    { label: 'GitHub', url: 'https://github.com/meinsta' },
  ],
  domains: [
    {
      id: 'systems',
      title: 'Systems',
      description: 'GTM systems · feature → proof → outcome',
      nodes: [
        {
          id: 'signup-engine',
          title: 'Signup engine',
          subtitle: 'Python, LLMs, RAG, GTM',
          feature: 'Content and sales-asset pipeline from transcripts and docs, with code gates that drop unsourced claims.',
          proof: 'Large reusable automation layer—tens of thousands of lines of Python, reusable skills, full test suite.',
          outcome: 'Rebuilt product page more than doubled signup rate; multiple pages and competitor teardowns shipped in a quarter.',
        },
        {
          id: 'workload-intel',
          title: 'Workload intel',
          subtitle: 'Competitive intelligence, cloud benchmarking',
          feature: 'Deal-pattern analysis plus cloud benchmarks on the workload class that decided major wins and a key loss.',
          proof: 'Multi-quarter win/loss review; selective-filter benchmarks showing multi-x speed advantages at large scale.',
          outcome: 'Battlecards and a data-backed roadmap gap with account implications for leadership.',
        },
        {
          id: 'enterprise-pitch',
          title: 'Enterprise pitch',
          subtitle: 'Messaging architecture, cross-functional GTM',
          feature: 'One enterprise narrative across fragmented security products, held from first call through technical deep dive.',
          proof: 'Cross-functional alignment with Product, Sales, and DevRel on a single story architecture.',
          outcome: 'Strategic content adoption +45%; identity-qualified pipeline +30%; supported multiple seven-figure pursuits.',
        },
      ],
    },
    {
      id: 'commissions',
      title: 'Commissions',
      description: 'Limited advisory · GTM engineering & AI orchestration',
      nodes: [
        { id: 'ai-engines', title: 'AI competitive engines' },
        { id: 'claim-verify', title: 'Claim verification systems' },
        { id: 'agent-skills', title: 'Custom agent skills' },
        { id: 'narrative', title: 'Technical narrative architecture' },
      ],
    },
    {
      id: 'collection',
      title: 'Collection',
      description: 'Hardware, ML, and installation works',
      nodes: [
        {
          id: 'beer-tap',
          title: 'Auth0 beer tap',
          subtitle: '2019 · IoT, device flow',
          note: 'IoT experiment combining Auth0 device-flow authentication with a physical beer tap—Raspberry Pi and Python.',
          video: { url: '/images/creative/beer.mp4', type: 'video/mp4' },
        },
        {
          id: 'soft-gears',
          title: 'Soft gears',
          subtitle: '2016 · Installation',
          note: 'Immersive 3D experience in a sewing studio, blending technology, feminism, and machine labor with user-controlled dynamic visualizations.',
          credit: {
            prefix: 'In collaboration with ',
            label: 'Rucyl',
            url: 'https://rucyl.com/about',
          },
          image: { url: '/images/creative/gears.jpg', alt: 'Soft Gears' },
          video: { url: '/images/creative/gears.mp4', type: 'video/mp4' },
        },
        {
          id: 'emoji',
          title: 'Obscure emoji translator',
          subtitle: '2016 · ML interface',
          note: 'Machine learning interface reimagining Google Translate: Dictionary of Obscure Sorrows definitions → emoji via Tumblr and Dango APIs.',
          image: { url: '/images/creative/emoji.jpg', alt: 'Emoji translator' },
          video: { url: '/images/creative/emoji-demo.mp4', type: 'video/mp4' },
        },
        {
          id: 'pillbox',
          title: 'Pillbox.0',
          subtitle: '2015 · Arduino',
          note: 'Unobtrusive pill-minder prototype (Arduino, Java): ambient light signals the schedule while pharmaceuticals stay discreetly hidden.',
          image: { url: '/images/creative/pillbox.gif', alt: 'Pillbox' },
        },
        {
          id: 'search-identities',
          title: 'Search identities',
          subtitle: '2016 · Search critique',
          note: 'Geo-localized search-result analysis exploring racial constructs and bias—blending top image results across engines to show what ranking systems encode.',
          image: { url: '/images/creative/meta-exhibition.JPG', alt: 'Search Identities' },
        },
        {
          id: 'volumetric',
          title: 'Volumetric',
          subtitle: '2016 · Face tracking',
          note: 'Experimental portraits with OpenFrameworks and face tracking—faces transformed into otherworldly volumetric 3D forms.',
          image: { url: '/images/creative/volumetric-1.gif', alt: 'Volumetric' },
        },
        {
          id: 'pointillism',
          title: 'Pointillism',
          subtitle: '2015 · p5.js',
          note: 'p5.js experiment in minimal computer vision: webcam → colored dots, microphone controls scale—especially alive when music is in the room.',
          image: { url: '/images/creative/pointilism.gif', alt: 'Pointillism' },
        },
      ],
    },
    {
      id: 'archive',
      title: 'Archive',
      description: 'Writing · why it mattered',
      nodes: [
        {
          id: 'acorn',
          title: 'Filtered vector search: ACORN',
          subtitle: 'Qdrant · 2026',
          url: 'https://qdrant.tech/articles/filtered-vector-search-acorn/',
          why: 'Makes filtered retrieval tradeoffs legible to builders—benchmark-backed narrative on ACORN vs filterable HNSW.',
        },
        {
          id: 'ai-buyers-guide',
          title: 'AI identity buyer’s guide',
          subtitle: 'Teleport · 2025',
          url: 'https://goteleport.com/blog/ai-buyers-guide/',
          why: 'Part of the first unified enterprise pitch system that lifted strategic content adoption 45% and identity-qualified pipeline 30%.',
        },
        {
          id: 'credential-hygiene',
          title: 'DevOps credential hygiene',
          subtitle: 'Teleport · 2025',
          url: 'https://goteleport.com/blog/devops-credential-hygiene-with-teleport/',
          why: 'Technical enablement content in the launch set that cut sales ramp ~35% and evaluation cycles ~25%.',
        },
        {
          id: 'mwi-ebook',
          title: 'Machine & workload identity ebook',
          subtitle: 'Teleport · 2025',
          url: 'https://goteleport.com/platform/machine-and-workload-identity/ebook/',
          why: 'Field-ready MWI narrative used to shorten identity evaluations and arm sellers on non-human identity risk.',
        },
        {
          id: 'nhi-whitepaper',
          title: 'Securing NHIs at the infrastructure layer',
          subtitle: 'Teleport · 2025',
          url: 'https://goteleport.com/resources/white-papers/securing-non-human-identities-nhis-at-the-infrastructure-layer',
          why: 'Core proof point in the enterprise identity story that supported multi-seven-figure pursuits.',
        },
        {
          id: 'mwi-brief',
          title: 'MWI runtime technical brief',
          subtitle: 'Teleport · 2025',
          url: 'https://goteleport.com/platform/machine-and-workload-identity/technical-brief/',
          why: 'Runtime identity brief used in technical deep dives after the unified pitch framework rolled out.',
        },
        {
          id: 'cdp-ciam',
          title: 'CDP and CIAM',
          subtitle: 'Okta · 2023',
          url: 'https://www.okta.com/blog/2023/12/cdp-and-ciam-a-match-made-in-cookieless-marketing-heaven/',
          why: 'Thought leadership in the CIAM program that drove ~35% YoY organic traffic and ~20% platform adoption lift.',
        },
        {
          id: 'zero-party',
          title: 'Zero-party data',
          subtitle: 'Okta · 2023',
          url: 'https://www.okta.com/blog/2023/09/what-is-zero-party-data-and-why-does-it-matter-now/',
          why: 'Category education tied to identity reference architectures that shortened sales cycles ~25%.',
        },
        {
          id: 'smart-fhir',
          title: 'SMART on FHIR best practices',
          subtitle: 'Auth0 · 2021',
          url: 'https://assets.ctfassets.net/2ntc334xpx65/3Fg4aWqeUq36j7ZMkG9rmW/a8594ad07e0335042ec13b1da1b9253d/Auth0-WP-SMART-Best-Practices.pdf',
          why: 'Healthcare identity guide in the set that improved SERP ~40% and inbound enterprise leads ~25%.',
        },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      description: 'Direct answers',
      nodes: [
        {
          id: 'who',
          title: 'Who is Meina Ghafouri?',
          subtitle: 'Identity',
          note: 'Product marketer at Qdrant focused on AI infrastructure and vector search. Builds GTM systems; engineering background and creative technologist. Previously Teleport, Okta, Auth0.',
        },
        {
          id: 'where',
          title: 'Where does she work?',
          subtitle: 'Role',
          note: 'Qdrant, as a product marketer. Previously product and technical marketing at Teleport, Okta, and Auth0.',
        },
        {
          id: 'write',
          title: 'What does she write about?',
          subtitle: 'Writing',
          note: 'Vector search, filtered retrieval, identity, machine/workload identity, developer-platform GTM—including a co-authored ACORN article on Qdrant.',
        },
        {
          id: 'contact',
          title: 'How do I contact her?',
          subtitle: 'Contact',
          note: 'meina.me · linkedin.com/in/meina-ghafouri · github.com/meinsta',
        },
      ],
    },
  ],
};

export default site;
